import { store } from '../../../store'
import { BroadSignFiltersModel } from '../../../api/broadSign/models/broad-sign-filters.model'
import { broadSignActions } from '../store/broad-sign-slice'
import api from '../../../api'
import { NotifierType } from '../../../components/Notifier'
import VariableUtils from '../../../utils/variable'
import { Company } from '../../../api/companies/types'
import { User } from '../../../types/user'
import { userFullName } from '../../../utils/user'
import { CampaignDepartment } from '../../../api/campaign/types'
import { BROADSIGN_CREATIONS_PER_PAGE } from '../../../constant'
import {
  BROADSIGN_ACCEPTANCE_NOT_SENT,
  BroadSignCreationAcceptanceModel,
  BroadSignCreationBuildingUsage,
} from '../../../api/broadSign/models/broad-sign-creation-acceptance.model'

// the same count the list is sorted and alerted on, read off the acceptances the tables are built
// from. holding it to those spares the list a reload it would reorder itself on
const usedBuildingsNotSentCount = (acceptances: BroadSignCreationAcceptanceModel[]) =>
  acceptances.filter(
    acceptance => acceptance.used && acceptance.acceptanceStatus === BROADSIGN_ACCEPTANCE_NOT_SENT
  ).length

export default class BroadSignService {
  static fetchBroadSignCreations = (
    filters: BroadSignFiltersModel,
    pageNumber: number
  ): Promise<unknown> => {
    return Promise.resolve(store.dispatch(broadSignActions.setPageNumber(pageNumber)))
      .then(() => Promise.resolve(store.dispatch(broadSignActions.setLoading(true))))
      .then(() => {
        const pageToFirst = pageNumber * BROADSIGN_CREATIONS_PER_PAGE

        return api.broadSign.fetchBroadSignCreations(filters, pageToFirst)
      })
      .then(response => {
        if (!response.data?.broadsignCreations) {
          return
        }

        const broadsignCreations = response.data.broadsignCreations.nodes.map(creation => {
          return {
            ...creation,
            advertiser: creation.advertiser
              ? VariableUtils.toSelectAsyncValue<Company>({
                  value: creation.advertiser.name,
                  rawData: creation.advertiser,
                })
              : null,
            commercialSupervisor: creation.commercialSupervisor
              ? VariableUtils.toSelectAsyncValue<User>({
                  label: userFullName(creation.commercialSupervisor),
                  value: creation.commercialSupervisor.id,
                  rawData: creation.commercialSupervisor,
                })
              : null,
            department: creation.department
              ? VariableUtils.toSelectAsyncValue<CampaignDepartment>({
                  value: creation.department.name,
                  rawData: creation.department,
                })
              : null,
          }
        })

        return store.dispatch(
          broadSignActions.setData({
            data: [...broadsignCreations],
            totalItemsCount: response.data.broadsignCreations.totalCount,
          })
        )
      })
      .catch(() => [])
      .finally(() => void store.dispatch(broadSignActions.setLoading(false)))
  }

  // the acceptances are the whole of what the tables show, every gallery of the creation is one of
  // them and the used flag it carries decides which table it lands in
  static fetchCreationAcceptances = (id: string): Promise<unknown> => {
    return Promise.resolve(store.dispatch(broadSignActions.setAcceptancesLoading(true)))
      .then(() => api.broadSign.fetchCreationAcceptances(id))
      .then(response => {
        const acceptances = response.data?.broadsignCreationAcceptances ?? []

        void store.dispatch(broadSignActions.setAcceptances(acceptances))

        return store.dispatch(
          broadSignActions.setUsedBuildingsNotSentCount({
            id,
            count: usedBuildingsNotSentCount(acceptances),
          })
        )
      })
      .catch(() => store.dispatch(broadSignActions.setAcceptances([])))
      .finally(() => void store.dispatch(broadSignActions.setAcceptancesLoading(false)))
  }

  static assignCreationMedia = (
    id: string,
    buildings: BroadSignCreationBuildingUsage[],
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string
  ): Promise<unknown> => {
    return (
      Promise.resolve(store.dispatch(broadSignActions.setAcceptancesLoading(true)))
        .then(() => api.broadSign.assignCreationMedia(id, buildings))
        .then(response => {
          const result = response.data?.assignBroadsignCreationMedia

          if (!result?.success) {
            addNotification(
              NotifierType.ERROR,
              result?.errors.length ? result.errors : t('broadSign.mediaAssignmentFailed')
            )

            return
          }

          addNotification(NotifierType.SUCCESS, t('broadSign.mediaAssignmentUpdated'))
        })
        .catch(() => addNotification(NotifierType.ERROR, t('broadSign.mediaAssignmentFailed')))
        // the used flag decides which table a gallery lands in, so the acceptances have to be
        // reloaded either way. the list is left alone on purpose: it is ordered on the count the
        // assignment has just changed and would reorder itself under the creation on screen, so it
        // keeps the order it was loaded with and only the alert of that creation is brought along
        .finally(() => BroadSignService.fetchCreationAcceptances(id))
    )
  }

  static sendCreationToAcceptance = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string
  ): Promise<unknown> => {
    return (
      api.broadSign
        .sendCreationToAcceptance(id)
        .then(response => {
          const result = response.data?.sendBroadsignCreationToAcceptance

          if (!result?.success) {
            addNotification(
              NotifierType.ERROR,
              result?.errors?.length ? result.errors : t('broadSign.sendToAcceptancesFailed')
            )

            return
          }

          addNotification(NotifierType.SUCCESS, t('broadSign.sendToAcceptancesSuccessful'))
        })
        .catch(() => addNotification(NotifierType.ERROR, t('broadSign.sendToAcceptancesFailed')))
        // the statuses the tables are built from have moved, the list keeps the order it was
        // loaded with and takes the new alert of the creation on screen off the reloaded
        // acceptances. the order the backend now gives waits for the next load of the list
        .finally(() => BroadSignService.fetchCreationAcceptances(id))
    )
  }

  static acceptCreation = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string,
    onStatusChange?: (status: 'accepted') => void
  ): Promise<unknown> => {
    return api.broadSign
      .acceptCreation(id)
      .then(response => {
        if (response.data?.broadsignAcceptCreation.broadsignCreation.status !== 'accepted') {
          return
        }

        void store.dispatch(broadSignActions.setCreationStatus({ id, status: 'accepted' }))

        addNotification(NotifierType.SUCCESS, t('broadSign.creationAccepted'))

        onStatusChange?.('accepted')
      })
      .catch(() => [])
  }

  static rejectCreation = (
    id: string,
    rerejectionReason: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string,
    setModalOpen: (open: boolean) => void,
    onStatusChange?: (status: 'rejected') => void
  ): Promise<unknown> => {
    return (
      api.broadSign
        .rejectCreation(id, rerejectionReason)
        .then(response => {
          if (response.data?.broadsignRejectCreation.broadsignCreation.status !== 'rejected') {
            return
          }

          void store.dispatch(broadSignActions.setCreationStatus({ id, status: 'rejected' }))

          addNotification(NotifierType.SUCCESS, t('broadSign.creationRejected'))

          setModalOpen(false)

          onStatusChange?.('rejected')
        })
        // the caller waits on this to take its spinner down, so a failed call has to settle it the
        // same as a successful one - as the acceptance above already does
        .catch(() => [])
    )
  }

  static updateBroadsignCreation = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string,
    filters: BroadSignFiltersModel,
    pageNumber: number,
    commercialSupervisorId?: string,
    departmentId?: string,
    advertiserId?: string
  ): Promise<unknown> => {
    return api.broadSign
      .updateBroadsignCreation(id, commercialSupervisorId, departmentId, advertiserId)
      .then(() => {
        addNotification(NotifierType.SUCCESS, t('broadSign.updateSuccessful'))
      })
      .then(() => {
        // Refresh the list after updating
        return BroadSignService.fetchBroadSignCreations(filters, pageNumber)
      })
      .catch(error => {
        console.error('Error updating BroadSign creation:', error)
        addNotification(NotifierType.ERROR, t('broadSign.updateFailed'))
      })
  }
}
